export type Rgb = readonly [number, number, number];

const WHITE: Rgb = [255, 255, 255];

function parseColor(color: string, malformedRgb: Rgb): Rgb {
  if (color.startsWith('rgb')) {
    const matches = color.match(/\d+/g);
    if (!matches) {
      return WHITE;
    }
    return matches.length >= 3
      ? [Number(matches[0]), Number(matches[1]), Number(matches[2])]
      : malformedRgb;
  }

  if (color.startsWith('#')) {
    const rawHex = color.slice(1);
    const hex =
      rawHex.length === 3
        ? rawHex
            .split('')
            .map((character) => character + character)
            .join('')
        : rawHex;
    const value = Number.parseInt(hex, 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  }

  return WHITE;
}

function linearChannel(value: number): number {
  const normalized = value / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function luminance([red, green, blue]: Rgb): number {
  return (
    0.2126 * linearChannel(red) +
    0.7152 * linearChannel(green) +
    0.0722 * linearChannel(blue)
  );
}

// `malformedRgb` gives the channels of an rgb() color with one or two numbers.
export function contrastRatio(
  first: string,
  second: string,
  malformedRgb: Rgb = WHITE
): number {
  const firstLuminance = luminance(parseColor(first, malformedRgb));
  const secondLuminance = luminance(parseColor(second, malformedRgb));
  const brightest = Math.max(firstLuminance, secondLuminance);
  const darkest = Math.min(firstLuminance, secondLuminance);
  return (brightest + 0.05) / (darkest + 0.05);
}
